import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { compose } from 'recompose';

import { useBulkActions } from './bulkActionsContext';
import { newTaskSelectors } from '../../../../state/newTask';
import { projectActions, projectSelectors } from '../../../../state/project';
import { getUsersForSelect } from '../../../../state/users/selectors';
import { ButtonsCollection, ConfirmModal, DropDown } from '../../../../ui-kit';
import { callNotification } from '../../../../utils/helpers/notifies';
import './style.sass';

const UNSET_ITEM_ID = '__unset__';

const BulkActionsBar = ({ t, projectId }) => {
  const dispatch = useDispatch();
  const {
    selectedIds,
    selectedCount,
    hasSelection,
    clearSelection,
    statusValue,
    setStatusValue,
    assigneeValue,
    setAssigneeValue,
    sprintValue,
    setSprintValue,
    moduleValue,
    setModuleValue,
    isModuleSelected,
  } = useBulkActions();

  const statusesEntities = useSelector(projectSelectors.getStatusesOfTasksEntities);
  const statusesList = useSelector(projectSelectors.getStatusesOfTasksList);
  const membersList = useSelector(projectSelectors.getProjectMembersList);
  const allUsers = useSelector(getUsersForSelect);
  const sprintsEntities = useSelector(projectSelectors.getProjectSprintsTasks);
  const modulesList = useSelector(newTaskSelectors.getModulesForDropdown);
  const isBulkDeletePending = useSelector(projectSelectors.getBulkDeleteProjectTasksPending);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const hasDropdownSelection = useMemo(
    () => !!statusValue || !!assigneeValue || !!sprintValue || isModuleSelected,
    [statusValue, assigneeValue, sprintValue, isModuleSelected],
  );

  const statuses = statusesList
    .map(statusId => statusesEntities[statusId])
    .filter(Boolean)
    .map(statusItem => ({ id: statusItem.id, label: statusItem.title }));

  const assigneeMembers = Object.values(allUsers)
    .filter(user => membersList.includes(user.value))
    .map(user => ({ id: user.value, label: user.label || '' }));

  const sprints = Object.values(sprintsEntities)
    .map(sprint => ({ id: sprint.id, label: sprint.title || t('Untitled sprint') }))
    .sort((a, b) => b.id - a.id);

  const modules = [
    { id: null, label: t('None') },
    ...modulesList.map(({ id, name }) => ({ id, label: name })),
  ];

  const handleSubmit = () => {
    dispatch(projectActions.bulkUpdateProjectTasksRequest(
      {
        projectId,
        taskIds: Array.from(selectedIds),
        ...(statusValue ? { status_id: statusValue } : {}),
        ...(assigneeValue ? { assignee: assigneeValue } : {}),
        ...(sprintValue ? { sprint_id: sprintValue } : {}),
        ...(isModuleSelected ? { project_module_id: moduleValue } : {}),
      },
      {
        callbacks: {
          success: () => {
            clearSelection();
            callNotification.success('Tasks updated successfully');
          },
        },
      },
    ));
  };

  const handleBulkDelete = () => {
    dispatch(projectActions.bulkDeleteProjectTasksRequest(
      {
        projectId,
        taskIds: Array.from(selectedIds),
      },
      {
        callbacks: {
          success: () => {
            setIsConfirmOpen(false);
            clearSelection();
            callNotification.success('Tasks deleted successfully');
          },
        },
      },
    ));
  };

  if (!hasSelection) {
    return null;
  }

  return (
    <div className="bulk-actions-bar">
      <span className="bulk-actions-bar__count">
        {t('{{count}} selected', { count: selectedCount })}
      </span>
      <div className="bulk-actions-bar__field">
        <span className="bulk-actions-bar__field-label">{t('Status')}</span>
        <DropDown
          className="bulk-actions-bar__dropdown"
          name="bulk-status"
          label=""
          placeholder={t('Select status')}
          list={statuses}
          currentItem={statusValue}
          onChange={({ val }) => setStatusValue(val.id)}
        />
      </div>
      <div className="bulk-actions-bar__field">
        <span className="bulk-actions-bar__field-label">{t('Assignee')}</span>
        <DropDown
          className="bulk-actions-bar__dropdown"
          name="bulk-assignee"
          label=""
          placeholder={t('Select assignee')}
          list={assigneeMembers}
          currentItem={assigneeValue}
          onChange={({ val }) => setAssigneeValue(val.id)}
        />
      </div>
      <div className="bulk-actions-bar__field">
        <span className="bulk-actions-bar__field-label">{t('Sprint')}</span>
        <DropDown
          className="bulk-actions-bar__dropdown"
          name="bulk-sprint"
          label=""
          placeholder={t('Select sprint')}
          list={sprints}
          currentItem={sprintValue}
          onChange={({ val }) => setSprintValue(val.id)}
        />
      </div>
      <div className="bulk-actions-bar__field">
        <span className="bulk-actions-bar__field-label">{t('Module')}</span>
        <DropDown
          className="bulk-actions-bar__dropdown"
          name="bulk-module"
          label=""
          placeholder={t('Select module')}
          list={modules}
          currentItem={isModuleSelected ? moduleValue : UNSET_ITEM_ID}
          onChange={({ val }) => setModuleValue(val ? val.id : null)}
        />
      </div>
      <ButtonsCollection.ButtonBrill
        className="button--sm button-brill--fill bulk-actions-bar__submit"
        onClick={handleSubmit}
        disabled={!hasDropdownSelection}
      >
        {t('Submit')}
      </ButtonsCollection.ButtonBrill>
      <ButtonsCollection.ButtonBrill
        className="button--sm bulk-actions-bar__delete"
        onClick={() => setIsConfirmOpen(true)}
      >
        {t('Delete')}
      </ButtonsCollection.ButtonBrill>
      <ButtonsCollection.ButtonBrill
        className="button--sm bulk-actions-bar__reset"
        onClick={clearSelection}
      >
        {t('Reset')}
      </ButtonsCollection.ButtonBrill>
      <ConfirmModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onAction={handleBulkDelete}
        title={t('Delete tasks')}
        actionTitle="Delete"
        isPending={isBulkDeletePending}
      >
        {t('Are you sure you want to delete {{count}} tasks?', { count: selectedCount })}
      </ConfirmModal>
    </div>
  );
};

BulkActionsBar.propTypes = {
  t: PropTypes.func.isRequired,
  projectId: PropTypes.number,
};

BulkActionsBar.defaultProps = {
  projectId: null,
};

export default compose(
  withTranslation(['tasks']),
)(BulkActionsBar);
